import React, { useEffect, useState, useMemo, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./EmployeeProfile.css";

const UPLOAD_BASE = "https://hrms.urbango.co.in/server/uploads/employees";


function buildCandidates(employeeNo) {
  const id = String(employeeNo || "").trim();
  if (!id) return ["https://via.placeholder.com/280x210?text=Profile"];
  return [
    `${UPLOAD_BASE}/${id}.jpg`,
    `${UPLOAD_BASE}/${id}.jpeg`,
    `${UPLOAD_BASE}/${id}.png`,
    `${UPLOAD_BASE}/${id}.webp`,
  ];
}

function ProgressiveImg({ employeeNo, className, alt = "Profile", style }) {
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const candidates = useMemo(() => buildCandidates(employeeNo), [employeeNo]);
  const src = failed
    ? "https://via.placeholder.com/280x210?text=Profile"
    : candidates[Math.min(idx, candidates.length - 1)];
  const onError = useCallback(() => {
    if (idx < candidates.length - 1) setIdx((i) => i + 1);
    else setFailed(true);
  }, [idx, candidates.length]);
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={failed ? undefined : onError}
    />
  );
}

function Row({ label, value, link }) {
  const v = value ?? "";
  if (!v && v !== 0) return null;
  if (link === "mailto") {
    return (
      <>
        <span className="k">{label}</span>
        <span className="v link">
          <a href={`mailto:${v}`}>{v}</a>
        </span>
      </>
    );
  }
  if (link === "tel") {
    return (
      <>
        <span className="k">{label}</span>
        <span className="v link">
          <a href={`tel:${v}`}>{v}</a>
        </span>
      </>
    );
  }
  return (
    <>
      <span className="k">{label}</span>
      <span className="v">{String(v)}</span>
    </>
  );
}

function Section({ title, children }) {
  return (
    <>
      <div className="section-title">{title}</div>
      <div className="kv">{children}</div>
    </>
  );
}

const TABS = [
  { key: "contact", label: "Contact" },
  { key: "work", label: "Work" },
  { key: "personal", label: "Personal" },
  { key: "identity", label: "Identity" },
  { key: "bank", label: "Bank" },
  { key: "permaddr", label: "Permanent Address" },
  { key: "curraddr", label: "Current Address" },
  { key: "family", label: "Family & Emergency" },
];

function EmployeeProfile({ username }) {
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState("");
  const [active, setActive] = useState("contact");

  useEffect(() => {
    if (!username) return;
    fetch(
      `https://hrms.urbango.co.in/server/index.php?endpoint=employee_profile&username=${encodeURIComponent(
        username
      )}`
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Failed"))))
      .then(setEmployee)
      .catch(() => {
        setEmployee(null);
        setError("Unable to load profile");
      });
  }, [username]);

  if (!employee) {
    return (
      <div className="profile-shell">
        <div className="profile-card">
          <span className="profile-loading">{error || "Loading profile..."}</span>
        </div>
      </div>
    );
  }

  const avatarSrc = employee.imageUrl ? employee.imageUrl : null;

  return (
    <div className="profile-shell">
      <div className="profile-card">
        <div className="profile-header">
          <div className="avatar-col">
            {avatarSrc ? (
              <img
                className="avatar-img"
                src={avatarSrc}
                alt={employee.employeeName || "Profile"}
              />
            ) : (
              <ProgressiveImg
                employeeNo={employee.employeeNo}
                alt={employee.employeeName || "Profile"}
                className="avatar-img"
              />
            )}
            <div className="avatar-meta">
              <h2 className="emp-name mb-1">{employee.employeeName}</h2>
              <div className="emp-sub">
                {employee.role} • {employee.department}
              </div>
              <div className="emp-id">Employee No: {employee.employeeNo}</div>
              <div className="emp-id">Date of Joining: {employee.DOJ}</div>
              <div className="emp-id">Employee Type: {employee.EmployeeType}</div>
            </div>
          </div>
         
        </div>

        {/* Tabs: left nav + right content */}
        <div className="tabs-grid">
          <nav className="tabs-rail">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`tab-rail-btn ${active === t.key ? "active" : ""}`}
                onClick={() => setActive(t.key)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="tabs-content">
            {active === "contact" && (
              <section className="about-grid">
                <Section title="Contact">
                  <Row label="Email" value={employee.officialEmail} link="mailto" />
                  <Row label="Phone" value={employee.phoneNumber} link="tel" />
                  <Row label="Location" value={employee.address} />
                </Section>
              </section>
            )}

            {active === "work" && (
              <section className="about-grid">
                <Section title="Work">
                  <Row label="Role" value={employee.role} />
                  <Row label="Department" value={employee.department} />
                  <Row label="Reporting To" value={employee.reporting} />
                  <Row label="Username" value={employee.username} />
                </Section>
              </section>
            )}

            {active === "personal" && (
              <section className="about-grid">
                <Section title="Personal">
                  <Row label="Gender" value={employee.Gender} />
                  <Row label="Blood Group" value={employee.bloodGroup} />
                  <Row label="Date of Birth" value={employee.DOB} />
                  <Row label="Date of Joining" value={employee.DOJ} />
                  <Row label="Nationality" value={employee.nationality} />
                  <Row label="Marital Status" value={employee.maritalStatus} />
                </Section>
              </section>
            )}

            {active === "identity" && (
              <section className="about-grid">
                <Section title="Identity">
                  <Row label="Aadhar No" value={employee.aadharNo} />
                  <Row label="PAN" value={employee.panCard} />
                  <Row label="Passport No" value={employee.passportNo} />
                  <Row label="UAN" value={employee.UAN} />
                  <Row label="Insurance No" value={employee.insuranceNo} />
                </Section>
              </section>
            )}

            {active === "bank" && (
              <section className="about-grid">
                <Section title="Bank">
                  <Row label="Bank Name" value={employee.bankName} />
                  <Row label="Account No" value={employee.accountNo} />
                  <Row label="IFSC" value={employee.ifsc} />
                </Section>
              </section>
            )}

            {active === "permaddr" && (
              <section className="about-grid">
                <Section title="Permanent Address">
                  <Row label="Address" value={employee.PermanentAddress} />
                  <Row label="City" value={employee.PermanentCity} />
                  <Row label="State" value={employee.State} />
                  <Row label="Pincode" value={employee.PermanentAddressPincode} />
                </Section>
              </section>
            )}

            {active === "curraddr" && (
              <section className="about-grid">
                <Section title="Current Address">
                  <Row
                    label="Address"
                    value={employee.CurrentAddress}
                  />
                  <Row
                    label="City"
                    value={employee.CurrentCity || employee.CurremtCity}
                  />
                  <Row label="State" value={employee.CurrentState} />
                  <Row label="Pincode" value={employee.CurrentAddressPINCode} />
                </Section>
              </section>
            )}

            {active === "family" && (
              <section className="about-grid">
                <Section title="Family & Emergency">
                  <Row label="Father's Name" value={employee.FatherName} />
                  <Row label="Mother's Name" value={employee.MotherName} />
                  <Row label="Spouse's Name" value={employee.SpouseName} />
                  <Row label="Emergency No 1" value={employee.EmergencyNo1} />
                  <Row label="Emergency No 2" value={employee.EmergencyNo2} />
                </Section>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeProfile;
